import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, Sidebar } from 'components/HeroSemantic';
import { Button as HeroUIButton } from 'vendor/heroui';
import { AddPasskeyModal, RecoveryCodesModal } from 'components';
import passkeyButtonClassName, { passkeyPrimaryButtonClassName } from 'components/Auth/passkeyButtonClassName';
import passkeyErrorMessageId from 'utils/auth/passkeyErrorMessageId';
import OutsideClickHandler from 'components/Common/OutsideClickHandler';
import usePasskey from 'hooks/Auth/usePasskey';
import useRecoveryStatusFetch from 'hooks/Auth/useRecoveryStatusFetch';
import usePush from 'hooks/router/usePush';
import { logout, markPasskeyRegistered } from 'state/modules/auth';
import { display, hide } from 'state/modules/dialog';
import Dialog from 'components/Dialog/Dialog';
import Footer from 'components/Footer/Footer';
import Loader from 'components/Common/Loader';
import { ClientManager, Header, MobileSidebar, NavigationBar, NotificationManager, Popup } from 'containers';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ChildrenRoutes from './ChildrenRoutes';
import IdleTimer from './IdleTimer';
import constants from '../../constants';
import '../../styles/heroui.css';

const App = ({ route }) => {
  const dispatch = useDispatch();
  const { admin, user, loggedIn, hasPasskey, passkeyRegistrationRequired, recoveryCodesRemaining } = useSelector(state => state.auth);
  const dialog = useSelector(state => state.dialog);
  const { loaderVisible } = useSelector(state => state.app);
  const { screenType } = useSelector(state => state.client);
  const location = useLocation();
  const tenant = useSelector(state => state.tenant);
  const { formatMessage, locale } = useIntl();
  const { register: registerPasskey, isBusy: passkeyBusy, error: passkeyError } = usePasskey();
  const fetchRecoveryStatus = useRecoveryStatusFetch();
  const { push } = usePush();

  const [displaySidebar, setDisplaySidebar] = useState(false);
  // FTR-3 pre-deadline nudge: shown once after a login where the account has
  // no passkey yet, but the mandatory deadline hasn't passed (that hard-block
  // case is the non-dismissible showPasskeyMandatory modal below instead).
  // Local-only, not persisted - reappears on the next login if skipped again.
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  // OWN-219: the nudge's "Set up a passkey" hands off to the same naming step
  // as the Security & Login page's own "Add Passkey", rather than registering
  // immediately with no name - see components/Auth/AddPasskeyModal.
  const [showAddPasskeyModal, setShowAddPasskeyModal] = useState(false);
  // Present only on the account's first passkey - shown once, then dismissed
  // via handleCodesDismissed. Registering from here (the login nudge/mandatory
  // modals) can happen from any page, so unlike the Security & Login page's
  // own Add Passkey flow, dismissing does not navigate anywhere - see
  // handleAddPasskeyContinue/handleCodesDismissed.
  const [revealedCodes, setRevealedCodes] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // Removing the account's last passkey (e.g. from the Passkeys settings
  // page) flips hasPasskey back to false mid-session - re-arm the nudge for
  // that transition specifically, rather than leaving it dismissed forever
  // just because it was dismissed once for a now-unrelated earlier state.
  const previousHasPasskeyRef = useRef(hasPasskey);
  useEffect(() => {
    if (previousHasPasskeyRef.current === true && hasPasskey === false) {
      setNudgeDismissed(false);
    }
    previousHasPasskeyRef.current = hasPasskey;
  }, [hasPasskey]);

  // App never unmounts between a session expiring and the owner logging back
  // in on the same /login route (logout() itself is a full page navigation,
  // but hitting an expired session mid-app is not) - so nudgeDismissed can
  // otherwise survive from an earlier dismissal into a brand new login.
  // Re-arm on every loggedIn:false -> true transition so the nudge is shown
  // on every login, not just the first one per browser tab.
  const previousLoggedInRef = useRef(loggedIn);
  useEffect(() => {
    if (!previousLoggedInRef.current && loggedIn) {
      setNudgeDismissed(false);
    }
    previousLoggedInRef.current = loggedIn;
  }, [loggedIn]);

  // Seeds the global low-codes banner below for pages other than Security &
  // Login itself - that page's own refresh() re-dispatches this too, and both
  // write to the same redux field, so either one keeps the other up to date.
  useEffect(() => {
    if (hasPasskey) {
      fetchRecoveryStatus();
    }
  }, [hasPasskey, fetchRecoveryStatus]);

  // Surfaces a failed registerPasskey() (e.g. the owner cancelled the browser
  // prompt, or biometric verification failed) - handleAddPasskeyContinue
  // already closed the modal by the time the ceremony resolves, so without
  // this the failure would otherwise be silently invisible.
  useEffect(() => {
    if (passkeyError) {
      dispatch(display(
        formatMessage({ id: 'notifications.title.error' }),
        formatMessage({ id: passkeyErrorMessageId(passkeyError) }),
        'error',
      ));
    }
  }, [passkeyError, dispatch, formatMessage]);

  const idleTimerRef = useRef(null);
  // Used by OutsideClickHandler so the floating hamburger toggle isn't counted
  // as an "outside" click — otherwise tapping it to close the sidebar would
  // fire close-then-open in the same gesture.
  const hamburgerRef = useRef(null);

  const { title, text, messageType, visible, closeAction: dialogCloseAction } = dialog;
  const { head: { titleTemplate, meta } } = tenant.config;

  const showSidebar = screenType === 'tablet' || screenType === 'mobile';
  const isLoginPage = /\/login$/.test(location.pathname);
  const isPasskeysPage = /\/security$/.test(location.pathname);

  useEffect(() => {
    setIsReady(true);
  }, []);

  // Mirror tenant CSS variables onto :root so that derived variables defined in
  // tailwind.css (e.g. --owner-header-bg: var(--tenant-secondary)) resolve against
  // the tenant's values. Setting them only on a descendant element does not work
  // because derived custom properties are computed at :root scope.
  useEffect(() => {
    if (typeof document === 'undefined') {return undefined;}
    const root = document.documentElement;
    root.style.setProperty('--tenant-primary', tenant.theme.colors.primary);
    root.style.setProperty('--tenant-secondary', tenant.theme.colors.secondary);
    root.style.setProperty('--tenant-max-width', `${tenant.theme.maxWidth}px`);
    return undefined;
  }, [tenant.theme.colors.primary, tenant.theme.colors.secondary, tenant.theme.maxWidth]);

  useEffect(() => {
    let storedTheme = null;
    if (typeof window !== 'undefined' && window.localStorage) {
      storedTheme = window.localStorage.getItem('owner-theme');
    }

    if (storedTheme === 'dark' || storedTheme === 'light') {
      document.documentElement.dataset.theme = storedTheme;
    }
  }, []);

  useEffect(() => {
    if (idleTimerRef.current && (user || admin)) {
      // Manually start the timer only after a user has logged in
      // Don't start the timer automatically neither when the component mounts because a page refresh will trigger a timeout and logged out the user
      idleTimerRef.current.start();
    }
  }, [user, admin]);

  const onIdle = () => {
    // End session if the user is idle
    if (user) {
      dispatch(logout());
    }
  };

  const toggleSidebar = () => {
    setDisplaySidebar(!displaySidebar);
  };

  const closeSidebar = () => {
    setDisplaySidebar(false);
  };

  const hideDialog = () => {
    if (dialogCloseAction) {
      dialogCloseAction();
    }
    dispatch(hide());
  };

  // OWN-229: hasPasskey/passkeyRegistrationRequired/recoveryCodesRemaining are
  // only ever populated at the logged-in account's own login/verify time (see
  // auth reducer's LOGIN_SUCCESS/VERIFY_SUCCESS/etc.) - selecting an owner via
  // /selectOwnerId only swaps state.auth.user (SET_USER), it never refetches
  // these. While impersonating, they still reflect htm.admin's OWN passkey
  // state, not the selected owner's, so every one of these banners/modals must
  // be suppressed for admins - otherwise an owner who already has a passkey
  // still gets nagged (or hard-blocked) because htm.admin itself has none.
  // Same isAdmin exemption already applied to this class of stale-while-
  // impersonating field in RenderRoute.js and Passkeys.js.
  const isAdmin = Boolean(admin);
  const showPasskeyNudge = !isAdmin && Boolean(user) && hasPasskey === false && !passkeyRegistrationRequired && !nudgeDismissed && !isPasskeysPage;
  // Same gating as the nudge modal, minus !nudgeDismissed - this stays up regardless
  // of whether the modal was skipped, since it's the persistent reminder. Superseded
  // by showPasskeyMandatory below once the deadline passes.
  const showPasskeyReminder = !isAdmin && Boolean(user) && hasPasskey === false && !passkeyRegistrationRequired && !isPasskeysPage;
  // FTR-3/OWN-223 post-deadline hard block - a non-dismissible modal (no onClose,
  // no skip action) overlaid on whatever route is already active, rather than a
  // redirect to a dedicated page. Excluded on Security & Login itself since that
  // page's own empty-state "Add Passkey" button already covers registering there.
  // Also suppressed while AddPasskeyModal is open (one overlay at a time, same as
  // the nudge's handleOpenAddPasskeyModal) - it reappears on its own if the naming
  // modal is cancelled or the ceremony fails, since hasPasskey is still false then.
  // OWN-232 passkey review: also suppressed while passkeyBusy - the naming
  // modal closes (setShowAddPasskeyModal(false)) synchronously, BEFORE the
  // awaited registerPasskey() call resolves, so without this the mandatory
  // modal would briefly re-satisfy its condition and flash back on top of
  // the WebAuthn prompt for the duration of that round trip.
  const showPasskeyMandatory = !isAdmin && Boolean(user) && hasPasskey === false && passkeyRegistrationRequired &&
    !isPasskeysPage && !showAddPasskeyModal && !passkeyBusy;
  // Mutually exclusive with the reminder above (that one only fires with no
  // passkey at all) - warns once a passkey exists but the recovery-code
  // batch has been fully used up.
  const showRecoveryCodesWarning = !isAdmin && Boolean(user) && hasPasskey === true && recoveryCodesRemaining === 0 && !isPasskeysPage;

  // Hides the nudge behind the naming modal rather than stacking both - once
  // the owner has engaged with setup, re-litigating the intro nudge for the
  // rest of this login isn't useful; the persistent banner below still covers
  // it if they cancel out of naming.
  const handleOpenAddPasskeyModal = () => {
    setNudgeDismissed(true);
    setShowAddPasskeyModal(true);
  };

  // The nudge (and the persistent banner) can pop up from anywhere in the app,
  // not just the Security & Login page - OWN-223 keeps the owner on whatever
  // page they were already on rather than forcing a navigation to Security &
  // Login, since that page (via OWN-224's step-up guard) may itself demand a
  // fresh secondary sign-in, turning a successful registration into a
  // confusing extra verification step.
  const handleAddPasskeyContinue = async deviceName => {
    setShowAddPasskeyModal(false);
    const { added, recoveryCodes } = await registerPasskey(deviceName);
    if (added) {
      dispatch(markPasskeyRegistered());
      // Present only on the account's first passkey - owner-api includes the
      // freshly issued batch in the register/verify response exactly once,
      // and it's never retrievable again.
      if (recoveryCodes) {
        setRevealedCodes(recoveryCodes);
      }
    }
  };

  const handleCodesDismissed = () => {
    setRevealedCodes(null);
  };

  return (
    <>
      <Helmet titleTemplate={titleTemplate} meta={meta} />
      <IdleTimer
        ref={idleTimerRef}
        crossTab
        timeout={constants.idleTimeout}
        syncTimers
        onIdle={() => onIdle()}
      />
      { (!isReady || loaderVisible) && <Loader />}
      { isReady && (
        <>
          {/* Menu sidebar for mobile view */}
          {showSidebar && (
            <OutsideClickHandler onOutsideClick={closeSidebar} ignoreRefs={[hamburgerRef]}>
              <MobileSidebar visible={displaySidebar} closeSidebar={closeSidebar} />
            </OutsideClickHandler>
          )}
          {
            // Floating mobile menu icon
            showSidebar && user && (
              <span ref={hamburgerRef} style={{ position: 'fixed', top: '12px', left: '12px', zIndex: 50 }}>
              <Button
                basic
                circular
                icon="sidebar"
                aria-label="Open navigation menu"
                onClick={() => toggleSidebar()}
                style={{
                  alignItems: 'center',
                  background: 'var(--owner-surface)',
                  border: '1px solid var(--owner-border-strong)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                  color: 'var(--owner-text)',
                  display: 'inline-flex',
                  height: '44px',
                  justifyContent: 'center',
                  padding: 0,
                  width: '44px',
                }}
              />
              </span>
            )
          }
          <Sidebar.Pushable
            as="div"
            style={{
              background: 'var(--owner-page-bg)',
              color: 'var(--owner-text)',
              minHeight: '100vh',
              margin: 0,
            }}
          >
            {/* Module for updating screen size information */}
            <ClientManager />
            {/* Module for displaying notifications */}
            <NotificationManager />
            {/* Module for displaying pop-up message */}
            <Popup />
            {/* Module for displaying message in a full screen dialog */}
            <Dialog
              open={visible}
              title={title}
              message={text}
              type={messageType}
              onClose={() => hideDialog()}
            />
            {/* FTR-3/OWN-223 pre-deadline nudge - skippable, unlike the post-deadline hard
                block. Copy is OWN-223's exact spec; the deadline date is hardcoded text
                here (not read from PASSKEY_MANDATORY_DEADLINE_DATE) since the frontend
                doesn't currently receive that value from the backend - keep this in sync
                by hand if the configured deadline ever changes. */}
            <Modal open={showPasskeyNudge} onClose={() => setNudgeDismissed(true)}>
              <Modal.Header>
                <FormattedMessage id="page.passkeyNudge.title" defaultMessage="Enhanced Security Action Required" />
              </Modal.Header>
              <Modal.Content>
                <p className="text-sm text-[var(--owner-text)]">
                  <FormattedMessage
                    id="page.passkeyNudge.description"
                    values={{ br: <br /> }}
                  />
                </p>
              </Modal.Content>
              <Modal.Actions>
                <HeroUIButton type="button" className={passkeyButtonClassName} onPress={() => setNudgeDismissed(true)}>
                  {formatMessage({ id: 'page.passkeyNudge.action.skip', defaultMessage: 'Remind Me Later' })}
                </HeroUIButton>
                <HeroUIButton type="button" isDisabled={passkeyBusy} className={passkeyPrimaryButtonClassName} onPress={handleOpenAddPasskeyModal}>
                  {formatMessage({ id: 'page.passkey.action.setup', defaultMessage: 'Register Passkey' })}
                </HeroUIButton>
              </Modal.Actions>
            </Modal>
            {/* FTR-3/OWN-223 post-deadline hard block - no onClose and no skip action, unlike
                the pre-deadline nudge above: registering a passkey or logging out are the only
                ways out. Reuses the same "Enhanced Security Action Required" copy the old
                dedicated /user/passkeyRequired page used. */}
            <Modal open={showPasskeyMandatory}>
              <Modal.Header>
                <FormattedMessage id="page.passkeyRequired.title" defaultMessage="Enhanced Security Action Required" />
              </Modal.Header>
              <Modal.Content>
                <p className="text-sm text-[var(--owner-text)]">
                  <FormattedMessage
                    id="page.passkeyRequired.description"
                    defaultMessage="Passkey registration is now mandatory. Set one up on this device using your biometrics, security key, or device PIN to continue - you won't be able to access other pages until you do."
                  />
                </p>
              </Modal.Content>
              <Modal.Actions>
                <HeroUIButton type="button" className={passkeyButtonClassName} onPress={() => dispatch(logout())}>
                  {formatMessage({ id: 'page.login.action.logout', defaultMessage: 'Log out' })}
                </HeroUIButton>
                <HeroUIButton type="button" isDisabled={passkeyBusy} className={passkeyPrimaryButtonClassName} onPress={() => setShowAddPasskeyModal(true)}>
                  {formatMessage({ id: 'page.passkey.action.setup', defaultMessage: 'Register Passkey' })}
                </HeroUIButton>
              </Modal.Actions>
            </Modal>
            <AddPasskeyModal
              open={showAddPasskeyModal}
              onCancel={() => setShowAddPasskeyModal(false)}
              onContinue={handleAddPasskeyContinue}
              isBusy={passkeyBusy}
            />
            <RecoveryCodesModal codes={revealedCodes} onDone={handleCodesDismissed} />
            {/* Main content */}
            <Sidebar.Pusher style={{ position: 'initial', paddingTop: showSidebar && user ? '68px' : 0 }}>
              { !showSidebar && !isLoginPage && <Header /> }
              { !showSidebar && user && <NavigationBar /> }
              {/* FTR-3 pre-deadline reminder - persistent (not dismissible), unlike the
                  login nudge modal above. Superseded by the showPasskeyMandatory modal
                  above once the deadline passes - the two are mutually exclusive. */}
              {showPasskeyReminder && (
                <div
                  className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface-muted)] p-3 text-sm text-[var(--owner-text)]"
                >
                  <span>
                    <FormattedMessage
                      id="page.passkeyReminder.description"
                      defaultMessage="Add a passkey to sign in faster and more securely."
                    />
                  </span>
                  <HeroUIButton type="button" className={passkeyButtonClassName} onPress={() => push('/security')}>
                    {formatMessage({ id: 'page.passkey.action.setup', defaultMessage: 'Register Passkey' })}
                  </HeroUIButton>
                </div>
              )}
              {/* Warns once the recovery-code batch is fully used up - a lost or reset
                  passkey device would otherwise leave the owner with no way back in. */}
              {showRecoveryCodesWarning && (
                <div
                  className="mx-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--owner-warning)] bg-[color-mix(in_srgb,var(--owner-warning)_12%,var(--owner-surface))] p-3 text-sm text-[var(--owner-text)]"
                >
                  <span>
                    <FormattedMessage
                      id="page.recoveryCodesWarning.description"
                      defaultMessage="You're out of recovery codes. Generate a new batch so you don't lose access if you lose your passkey device."
                    />
                  </span>
                  <HeroUIButton type="button" className={passkeyButtonClassName} onPress={() => push('/security')}>
                    {formatMessage({ id: 'page.passkeys.action.generateRecovery', defaultMessage: 'Generate Backup Codes' })}
                  </HeroUIButton>
                </div>
              )}
              {/* Child routes */}
              <ChildrenRoutes
                admin={admin}
                route={route}
                locale={locale}
                tenantId={tenant.id}
                user={user}
              />
              { !showSidebar && !isLoginPage && <Footer /> }
            </Sidebar.Pusher>
          </Sidebar.Pushable>
        </>
      )}
    </>
  );
};

App.propTypes = {
  route: PropTypes.object,
};

export default App;
